<template>
  <mpf-form
    :hint="formVerifyDateOfBirth.config.hint"
    :fields="formVerifyDateOfBirth.fields"
    :form-errors="formErrors"
    submit-label="Continue"
    @submit="(e) => handleSubmit(e)"
  />
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import { router } from '../../router';
  import { formVerifyDateOfBirth } from '../../data/forms';
  import { useMessageThreadStore } from '../../stores/useMessageThreadStore';
  import { useErrorStore } from '../../stores/useErrorStore';

  type FormError = { name: string; errors: string[] };

  const route = useRoute();
  const messageThreadStore = useMessageThreadStore();
  const errorStore = useErrorStore();

  const formErrors = ref<FormError[]>([]);

  const messageId = computed(() => String(route.params.messageId ?? ''));

  // The thread is NOT fetched here. `api_patient_portal_message_get` requires an
  // authenticated patient session, which only the challenge below establishes — so
  // the heading cannot carry the patient's first name at this step.
  onMounted(() => messageThreadStore.setMessageId(messageId.value));

  const handleSubmit = async (data: any) => {
    if (!data.valid) return;

    const result = await messageThreadStore.verifyDateOfBirth(data.values.dateOfBirth);

    if (result.ok) {
      formErrors.value = [];
      await messageThreadStore.loadThread(messageId.value);
      // patchTenantAwarePush prefixes the tenant, so push the flow-relative path.
      await router.push(`/messages/${messageId.value}/message`);
      return;
    }

    // `date-of-birth-mismatch` is redirectToError: false, so it renders here rather
    // than throwing the patient out to the error page mid-flow.
    errorStore.setErrors(result.errors);
    const message = errorStore.getFirstMessage();
    formErrors.value = [
      {
        name: 'dateOfBirth',
        errors: [
          message && message.trim()
            ? message
            : 'The date of birth you entered does not match our records.',
        ],
      },
    ];
  };
</script>
